"use client"

import { createContext, useContext, useEffect, useState } from "react"
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth"
import { auth } from "../config/firebase"
import { ref, set, update, query, orderByChild, onValue, serverTimestamp, child, get } from "firebase/database"
import { database } from "../config/firebase"
import Swal from "sweetalert2"
import { error1, error2 } from "../constants/codeValidationErrors"
import { createAstronomyTextForUser } from "../utils/createAstronomyText"
import { equalTo } from "firebase/database" // Declare the equalTo variable
import { useTranslations } from "../translations"

// Mapea un código de error (numérico o de Firebase auth/*) a su clave de traducción.
const ERROR_KEY_BY_CODE = {
  1: "errInvalidCode",
  2: "errCodeInUse",
  "auth/email-already-in-use": "errEmailInUse",
  "auth/user-not-found": "errUserNotFound",
  "auth/invalid-email": "errInvalidEmail",
  "auth/too-many-requests": "errTooManyRequests",
  "auth/missing-email": "errMissingEmail",
}

/* import Loading from '../components/Loading'; */

// Base del backend (mismo host que el resto de llamadas al API de ai-blooms).
const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "")

const UserContext = createContext()
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer)
    toast.addEventListener("mouseleave", Swal.resumeTimer)
  },
})

export const AuthContextProvider = ({ children }) => {
  const { t } = useTranslations()
  const [user, setUser] = useState(undefined)
  const [percent, setPercent] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isWeb, setIsWeb] = useState(false)
  const [thereIsLearner, setThereIsLearner] = useState(false)

  // Traduce un error por su código; cae a "errUnexpected" si no está mapeado.
  const tError = (code) => t(ERROR_KEY_BY_CODE[code] || "errUnexpected")

  const validateCode = (code) => {
    if (code === error1.code || code === error2.code) {
      Toast.fire({ icon: "error", title: tError(code) })
    }
  }

  const createUser = async (data) => {
    setLoading(true)
    /* console.log(data); */
    if (!data.avatar_file) {
      setLoading(false) // evita que el overlay de carga quede colgado
      Toast.fire({ icon: "error", title: t("errAvatarRequired") })
      return
    }
    if (!data.role) {
      setLoading(false)
      Toast.fire({ icon: "error", title: t("errRoleRequired") })
      return
    }

    searchCode(data)
    /* validateCode(); */
    /* console.log(error); */
    /* if(error===false) {
        const codeRef = query( ref(database, 'accesscodes'), orderByChild('code'), equalTo(data.code) );
        console.log("code access", codeRef);
          onValue(codeRef, (snapshot) => {
            let itemKey
            snapshot.forEach(item => {
              itemKey = item.key
            })
        });
        
      } */
  }
  const create = (data, itemKey) => {
    /* console.log("Sin errores", data);
    console.log("Data Item", itemKey); */
    // progress can be paused and resumed. It also exposes progress updates.
    // Receives the storage reference and the file to upload.
    createUserEmail(data, itemKey)
  }

  const createUserEmail = async (data, itemKey) => {
    // Orden importante para NO dejar cuentas huérfanas:
    // 1) subir imagen (Cloudinary) → 2) crear cuenta → 3) guardar perfil.
    // Si un paso posterior falla y la cuenta ya se creó, se elimina.
    let createdUser = null
    try {
      // 1) Imagen primero (aún no existe la cuenta): evita la cuota de Firebase Storage.
      setPercent(30)
      const cloudForm = new FormData()
      cloudForm.append("file", data.avatar_file)
      cloudForm.append("upload_preset", "miabc_online")
      const cloudRes = await fetch("https://api.cloudinary.com/v1_1/dbohppph0/image/upload", {
        method: "POST",
        body: cloudForm,
      })
      if (!cloudRes.ok) throw new Error("avatar-upload")
      const url = (await cloudRes.json()).secure_url
      setPercent(60)

      // 2) Crear la cuenta. Si el correo ya existe, falla AQUÍ sin dejar datos a medias.
      const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password)
      createdUser = userCredential.user
      setPercent(85)

      // 3) Guardar perfil y marcar el código como usado.
      await set(ref(database, "users/" + createdUser.uid), {
        accessCode: data.code,
        email: data.email,
        profileImageLink: url,
        role: data.role,
      })
      updateCode(itemKey)
    } catch (err) {
      setLoading(false)
      // Rollback: si la cuenta llegó a crearse pero falló un paso posterior, la borramos
      // para que el correo NO quede bloqueado ("ya está en uso") sin registro real.
      if (createdUser) {
        try { await createdUser.delete() } catch (_) {}
      }
      // Error de imagen → errAvatarUpload; error de Firebase Auth → mensaje por código.
      const title = err && err.code ? tError(err.code) : t("errAvatarUpload")
      Toast.fire({ icon: "error", title })
    }
  }

  const updateCode = (itemKey) => {
    update(ref(database, "accesscodes/" + itemKey), {
      value_web: true,
    })
      .then(() => {
        const dbRef = ref(database)
        get(child(dbRef, "accesscodes/" + itemKey)).then((snapshot) => {
          if (snapshot.exists()) {
            const codeData = snapshot.val()
            const userAppId = codeData.id

            // Crear texto de astronomía para el nuevo usuario
            createAstronomyTextForUser(auth.currentUser.uid, userAppId)
              .then((result) => {
                if (result.success) {
                  console.log("[v0] Astronomy text created for new user")
                } else {
                  console.error("[v0] Failed to create astronomy text:", result.error)
                }
              })
              .catch((error) => {
                console.error("[v0] Error creating astronomy text:", error)
              })
          }
        })

        setLoading(false)
        window.location.replace("/account")
      })
      .catch((error) => {
        setLoading(false)
      })
  }

  const searchCode = (data) => {
    /* const error01 = 1;
    const error02 = 2; */
    const codeRef = query(ref(database, "accesscodes"), orderByChild("code"), equalTo(data.code))
    /* console.log("code access", codeRef); */
    onValue(
      codeRef,
      (snapshot) => {
        /* console.log(snapshot); */
        let itemKey
        let dataItem
        snapshot.forEach((item) => {
          itemKey = item.key
          dataItem = item.val()
        })
        if (itemKey !== undefined && itemKey) {
          /* console.log("Por válido itemKey entró, itemkey", itemKey);    */
          /* console.log("Antes de entrar al error 2", dataItem.value); */
          if (dataItem.value_web === true) {
            setLoading(false)
            /* console.log("llegó error 2", dataItem.value_web); */
            validateCode(error2.code)
            /* console.log("Entra por codigo usado, value : " + dataItem.value_web); */
            /*  setError(error2.code); */
            /* return error2; */
          }
          if (!dataItem.value_web) {
            create(data, itemKey)
          }

          /* return itemKey; */
        } else {
          setLoading(false)
          validateCode(error1.code)
          /* setError(error1.code); */
          /* console.log("Por undefined itemKey entró"); */
        }

        /* console.log("llegó", error01); */
        /* return error1; */
      },
      {
        onlyOnce: true,
      },
    )
  }
  /* const signIn = async (email, password) => {
    //try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
      setLoading(false);
    //} catch (err) {
    //  console.error(err);
    //  setLoading(false);
    //}
  }; */

  // ¿El padre tiene al menos un aprendiz (hijo) registrado bajo su mismo código?
  // Lectura ÚNICA (get) y determinista: devuelve boolean. No cierra sesión ni
  // muta estado; el llamador decide a dónde enrutar (dashboard vs. onboarding).
  const validateThereIs = async (userId) => {
    const dbRef = ref(database)
    const codeSnap = await get(child(dbRef, "users/" + userId + "/accessCode"))
    const accessCode = codeSnap.val()
    if (!accessCode) return false
    const res = await get(query(ref(database, "users"), orderByChild("accessCode"), equalTo(accessCode)))
    let found = false
    if (res.exists()) {
      res.forEach((item) => {
        if (item.val().username) found = true
      })
    }
    return found
  }

  const validateWebUser = async (userId) => {
    return new Promise((resolve, reject) => {
      const dbRef = ref(database)
      get(child(dbRef, "users/" + userId)).then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val()
          if (data.role === "Padre" || data.role === "Maestro") {
            setIsWeb(true)
            resolve(true)
          } else if (data.role === "Aprendiz" || data.role === "aprendiz") {
            reject({ code: "noAccess" })
          } else if (!data.username) {
            setIsWeb(true)
            resolve(true)
          } else {
            reject({ code: "noAccess" })
          }
        } else {
          reject({ code: "noAccess" })
        }
      }).catch(err => reject(err))
    })
  }

  const saveLog = (userId) => {
    /* console.log(userId); */
    const dbRef = ref(database)
    get(child(dbRef, "users/" + userId + "/logs")).then((snapshot) => {
      let arr = []
      if (snapshot.exists()) {
        const data = snapshot.val()
        /*  console.log(data); */
        arr = data
        arr.push({
          userId: userId,
          timestamp: serverTimestamp(),
        })
      } else {
        arr.push({
          userId: userId,
          timestamp: serverTimestamp(),
        })
      }
      set(ref(database, "users/" + userId + "/logs"), arr)
    })
  }

  /* const userInfo = (userId) => {
  
    const userRes = ref(database, 'users/' + userId );
    onValue(userRes, (snapshot) => {
      const data = snapshot.val();
      console.log(data)
      setUser({...user, info:data});
    }); 
  } */

  const passwordRecovery = async (email) => {
    try {
      setLoading(true)
      await sendPasswordResetEmail(auth, email)
      setLoading(false)
    } catch (err) {
      /* console.error(err); */
      setError(ERROR_KEY_BY_CODE[err.code] ? tError(err.code) : t("errCheckConnection"))
      setLoading(false)
    }
  }

  // Resuelve el destino de un usuario YA autenticado (por contraseña u OTP).
  // Devuelve { isAdmin } | { role } | { isAdmin:false, hasLearner } y, si el
  // usuario no tiene acceso al portal, cierra sesión y relanza el error.
  const resolveWebSession = async (user) => {
    // Cuentas de plataforma/institucionales (admin, escuela, profesor) no son
    // Padre/Maestro-con-alumnos: saltan la validación de "aprendiz asociado".
    const tokenResult = await user.getIdTokenResult()
    const claimRole = tokenResult.claims && tokenResult.claims.role
    if (tokenResult.claims && tokenResult.claims.admin === true) {
      setIsWeb(true)
      return { isAdmin: true }
    }
    if (claimRole === "escuela") {
      setIsWeb(true)
      return { role: "escuela" }
    }
    if (claimRole === "maestro") {
      setIsWeb(true)
      return { role: "maestro" }
    }
    if (claimRole === "representante") {
      setIsWeb(true)
      return { role: "representante" }
    }

    try {
      await validateWebUser(user.uid)
    } catch (err) {
      await signOut(auth);
      throw err;
    }
    // Padre válido: entra SIEMPRE. Si aún no registró a su aprendiz en la app,
    // no lo expulsamos: el flujo lo lleva a /onboarding para guiarlo.
    const hasLearner = await validateThereIs(user.uid)
    setThereIsLearner(hasLearner)
    return { isAdmin: false, hasLearner }
  }

  const signIn = async (email, password) => {
    const userCredentials = await signInWithEmailAndPassword(auth, email, password)
    return resolveWebSession(userCredentials.user)
  }

  // ── Inicio de sesión con código OTP por correo (alternativa a la contraseña) ──
  // Pide al backend que envíe un código de un solo uso al correo. No revela si la
  // cuenta existe; en caso de cooldown adjunta retryAfter (segundos) al error.
  const requestOtp = async (email, lang) => {
    const res = await fetch(`${API_BASE}/api/auth/otp/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, lang }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      const err = new Error(data.error || "otp-request-failed")
      err.code = data.error
      err.retryAfter = data.retryAfter
      throw err
    }
    return data // { ok, cooldown }
  }

  // Verifica el código: el backend devuelve un custom token, iniciamos sesión con
  // él y reutilizamos la misma resolución de rol/aprendiz que el login normal.
  const verifyOtp = async (email, code) => {
    const res = await fetch(`${API_BASE}/api/auth/otp/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.token) {
      const err = new Error(data.error || "otp-verify-failed")
      err.code = data.error
      err.remaining = data.remaining
      throw err
    }
    const cred = await signInWithCustomToken(auth, data.token)
    return resolveWebSession(cred.user)
  }

  const logout = async () => {
    try {
      setLoading(true)
      await signOut(auth)
      if (thereIsLearner && isWeb) {
        setIsWeb(false)
        setThereIsLearner(false)
      }
      setLoading(false)
      window.location.href = "/login"
    } catch (err) {
      /* console.error(err); */
      setLoading(false)
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      /* console.log(currentUser); */
      setUser(currentUser)
      /* setLoading(false); */
    })
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (isWeb) {
      saveLog(user.uid)
    }
  }, [isWeb])

  /* if(loading)
    return <Loading/> */
  return (
    <UserContext.Provider
      value={{ createUser, user, logout, signIn, requestOtp, verifyOtp, passwordRecovery, percent, loading, error, thereIsLearner, isWeb }}
    >
      {children}
    </UserContext.Provider>
  )
}

export const UserAuth = () => {
  return useContext(UserContext)
}
